import { useState } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import logo from '../images/nav_logo.png';
import { FaChevronDown } from 'react-icons/fa';

const links = [
  {
    name: 'Team',
    path: '/departments',
    section: 'team',
    subsections: [
      { name: 'Departments', path: '/departments#departments' },
      { name: 'Members', path: '/departments#members' },
      { name: 'Recruitment', path: '/departments#recruitment' },
    ],
  },
  {
    name: 'Atlântico',
    path: '/atlantico',
    section: 'atlantico',
    subsections: [
      { name: 'Prototype', path: '/atlantico' },
      { name: 'Open Source', path: '/opensource' },
    ],
  },
  {
    name: 'Events',
    path: '/events',
    section: 'events',
    subsections: [
      { name: 'Competitions', path: '/events' },
      { name: 'Awards', path: '/events#awards' },
      { name: 'Exhibitions', path: '/events#exhibitions' },
      { name: 'Gallery', path: '/events#gallery' },
    ],
  },
  {
    name: 'News',
    path: '/news',
    section: 'news',
    subsections: [
      { name: 'Newsletter', path: '/news#newsletter' },
    ],
  },
  {
    name: 'Sponsors',
    path: '/sponsors',
    section: 'sponsors',
    subsections: [
      { name: 'Platinum', path: '/sponsors#tier-Platinum' },
      { name: 'Gold', path: '/sponsors#tier-Gold' },
      { name: 'Silver', path: '/sponsors#tier-Silver' },
      { name: 'Bronze', path: '/sponsors#tier-Bronze' },
      { name: 'General', path: '/sponsors#tier-General' },
      { name: 'Software', path: '/sponsors#tier-Software' },
      { name: 'Institutional', path: '/sponsors#tier-Institutional' },
      { name: 'Partners', path: '/sponsors#tier-Partners' },
      { name: '---', separator: true },
      { name: 'Support Us', path: '/#support-us', isSupport: true },
    ],
  },
];

const NavBar = ({ activeSection }) => {
  const [menuOpen, setMenuOpen] = useState(false);
  const [activeDropdown, setActiveDropdown] = useState(null);
  const navigate = useNavigate();
  const location = useLocation();

  const handleNavClick = (link) => {
    if (link.section && location.pathname === '/') {
      const element = document.getElementById(link.section);
      if (element) element.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(link.path);
    }
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleSubClick = (path) => {
    const [p, hash] = path.split('#');
    if (location.pathname === p && hash) {
      const el = document.getElementById(hash);
      if (el) el.scrollIntoView({ behavior: 'smooth' });
    } else {
      navigate(path);
      if (hash) {
        setTimeout(() => {
          const el = document.getElementById(hash);
          if (el) el.scrollIntoView({ behavior: 'smooth' });
        }, 300);
      }
    }
    setMenuOpen(false);
    setActiveDropdown(null);
  };

  const handleLogoClick = (e) => {
    e.preventDefault();
    if (location.pathname !== '/') {
      navigate('/');
    } else {
      window.scrollTo({ top: 0, behavior: 'smooth' });
    }
    setMenuOpen(false);
  };

  const isActive = (link) => {
    if (location.pathname === link.path) return true;
    if (location.pathname === '/' && activeSection === link.section) return true;
    if (location.hash === `#${link.section}`) return true;
    return false;
  };

  const renderSubsectionButton = (sub, isMobile = false) => {
    if (sub.separator) {
      return <div key="sep" className={`${isMobile ? 'mx-4' : 'mx-2'} my-1 h-px bg-mainBlue/10`} />;
    }
    return (
      <button
        key={sub.name}
        onClick={() => handleSubClick(sub.path)}
        className={`w-full text-left px-4 py-2 text-sm transition-colors ${
          sub.isSupport
            ? 'text-mainBlue font-medium'
            : 'text-mainBlue/70 hover:text-mainBlue hover:bg-mainBlue/5'
        }`}
      >
        {sub.name}
      </button>
    );
  };

  return (
    <nav className="fixed top-0 left-0 w-full z-[1000] bg-white/95 backdrop-blur-md border-b border-mainBlue/10 shadow-sm">
      <div className="flex items-center justify-between px-4 md:px-8 h-16 max-w-7xl mx-auto">
        
        {/* Logo */}
        <button onClick={handleLogoClick} className="py-1 focus:outline-none relative z-[1001]">
          <img src={logo} alt="FAST" className="h-8 md:h-10 w-auto" />
        </button>

        {/* Desktop Links - always rendered, hidden on mobile via CSS */}
        <div className="hidden lg:flex items-center gap-1">
          {links.map((link) => (
            <div
              key={link.name}
              className="relative"
              onMouseEnter={() => link.subsections && setActiveDropdown(link.name)}
              onMouseLeave={() => setActiveDropdown(null)}
            >
              <button
                onClick={() => handleNavClick(link)}
                className={`px-4 py-2 rounded-lg text-sm font-medium transition-all duration-300 flex items-center gap-1 ${
                  isActive(link)
                    ? 'text-mainBlue bg-mainBlue/10'
                    : 'text-mainBlue/80 hover:text-mainBlue hover:bg-mainBlue/5'
                }`}
              >
                {link.name}
                {link.subsections && (
                  <FaChevronDown size={10} className={`shrink-0 transition-transform ${activeDropdown === link.name ? 'rotate-180' : ''}`} />
                )}
              </button>

              {link.subsections && activeDropdown === link.name && (
                <div className="absolute top-full left-1/2 -translate-x-1/2 pt-2 bg-transparent z-50">
                  <div className="bg-white/95 backdrop-blur-md rounded-xl shadow-lg border border-mainBlue/10 py-2 min-w-40">
                    {link.subsections.map((sub) => renderSubsectionButton(sub))}
                  </div>
                </div>
              )}
            </div>
          ))}
        </div>

        {/* Mobile hamburger button - always rendered, hidden on desktop via CSS */}
        <button
          onClick={() => setMenuOpen(!menuOpen)}
          className="lg:hidden ml-auto p-2 rounded-md hover:bg-mainBlue/10 transition-colors focus:outline-none text-mainBlue relative z-[1001] flex items-center justify-center"
          aria-label="Toggle menu"
        >
          <svg width="24" height="24" viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="2.5" strokeLinecap="round" strokeLinejoin="round">
            <line x1="3" y1="6" x2="21" y2="6" />
            <line x1="3" y1="12" x2="21" y2="12" />
            <line x1="3" y1="18" x2="21" y2="18" />
          </svg>
        </button>
      </div>

      {/* MOBILE DROPDOWN MENU */}
      <div 
        className={`lg:hidden absolute top-16 left-0 w-full bg-white border-b border-mainBlue/10 shadow-2xl transition-all duration-300 ease-in-out ${
          menuOpen ? 'opacity-100 translate-y-0 visible' : 'opacity-0 -translate-y-2 invisible'
        }`}
        style={{ zIndex: 999 }}
      >
        <div className="px-4 py-6 space-y-2 max-h-[80vh] overflow-y-auto">
          {links.map((link) => (
            <div key={link.name} className="border-b border-slate-50 last:border-none pb-2">
              <button
                onClick={() => handleNavClick(link)}
                className={`w-full text-left px-4 py-3 rounded-lg text-lg font-semibold flex items-center justify-between ${
                  isActive(link) ? 'text-mainBlue bg-mainBlue/5' : 'text-slate-700'
                }`}
              >
                {link.name}
                {link.subsections && <FaChevronDown size={12} className="opacity-40" />}
              </button>
              
              {link.subsections && (
                <div className="mt-1 ml-4 grid grid-cols-1 gap-1">
                  {link.subsections.map((sub) => renderSubsectionButton(sub, true))}
                </div>
              )}
            </div>
          ))}
        </div>
      </div>
    </nav>
  );
};

export default NavBar;